import {useDataTracking} from '#imports';
import {
    type AISummaryContext,
    type AISummaryFeedbackType,
    type AISummaryProfileType,
    type AISummaryStateAction,
    ProfileViewMode,
} from '~/config/TrackingEvents';
import {
    type EditionOpenedEntryPoint,
    EditionOpenedLabel,
    type EditionOpenedSection,
    type EditionOpenedWorkflow,
    type ProfileViewedOrigin,
    type SocialShareEntryPoint,
    TrackingEvents,
    type TranslationAction,
    type ProfileUpdatedSection,
    type ProfileSectionReachedSection,
    type ProfileSectionReachedEntryPoint,
    type ProfileSectionReachedContext,
    ProfileUpdatedElementStatus,
    type InfluenceLinkType,
    type RecommendationRequestStartedEntryPoint,
    type RecommendationDeletedBy,
    type RecommendationEmailLinkOpenedRecommenderStatus,
} from '~/config/TrackingEvents';
import type {CompletionStatus} from '~/features/experience/store/experiencesEditStore';
import type {Profile} from '~/types/domain/Profile';

export function useProfileTracking() {
    const {track} = useDataTracking();

    function trackProfileViewed(
        searchId: string,
        advancedSearchId: string,
        sourcingHubCatalogId: string,
        accountId: string | undefined,
        origin: ProfileViewedOrigin | undefined,
        mode: ProfileViewMode,
        isOwner: boolean,
    ) {
        track(TrackingEvents.PROFILE_VIEWED, {
            search_event_id: searchId,
            advanced_search_id: advancedSearchId,
            catalog_id: sourcingHubCatalogId,
            freelancer_account_id: accountId,
            origin,
            mode,
            is_owner: isOwner,
        });
    }

    function trackAISummaryFeedbackClicked(
        profileType: AISummaryProfileType,
        context: AISummaryContext,
        profileId: string,
        feedbackType: AISummaryFeedbackType,
    ) {
        track(TrackingEvents.AI_SUMMARY_FEEDBACK_CLICKED, {
            profile_type: profileType,
            context,
            profile_id: profileId,
            feedback: feedbackType,
        });
    }

    function trackAISummaryFeedbackSubmitted(
        profileType: AISummaryProfileType,
        context: AISummaryContext,
        profileId: string,
        negativeReasons: string[],
    ) {
        track(TrackingEvents.AI_SUMMARY_FEEDBACK_SUBMITTED, {
            profile_type: profileType,
            context,
            profile_id: profileId,
            negative_reasons: negativeReasons.join(', '),
        });
    }

    function trackAISummaryStateAction(
        profileType: AISummaryProfileType,
        context: AISummaryContext,
        profileId: string,
        sectionStateAction: AISummaryStateAction,
    ) {
        track(TrackingEvents.AI_SUMMARY_INTERACTIONS, {
            profile_type: profileType,
            context,
            profile_id: profileId,
            state_action: sectionStateAction,
        });
    }

    function trackAISummaryViewed(profileType: AISummaryProfileType, context: AISummaryContext, profileId: string) {
        track(TrackingEvents.AI_SUMMARY_VIEWED, {
            profile_type: profileType,
            context,
            profile_id: profileId,
        });
    }

    function trackFAQOpened(profileId: string) {
        track(TrackingEvents.FAQ_OPENED, {
            source: 'ai_summary',
            profile_id: profileId,
        });
    }

    function trackProfileEdition(
        section: EditionOpenedSection,
        entryPoint: EditionOpenedEntryPoint,
        workflow: EditionOpenedWorkflow,
        existingElement: boolean,
        label?: EditionOpenedLabel,
    ) {
        track(TrackingEvents.PROFILE_EDITION_OPENED, {
            profile_section: section,
            entry_point: entryPoint,
            workflow,
            existing_element: existingElement,
            label,
        });
    }

    function trackProfileTranslated(profile: Profile, section: string, outputLanguage: string, action: TranslationAction) {
        track(TrackingEvents.TRANSLATION, {
            profile_section: section,
            input_language: profile.dominantLanguageDetected,
            output_language: outputLanguage,
            action,
            freelancer_account_id: profile.accountId,
        });
    }

    function trackProfileUpdateCardViewed(section: EditionOpenedSection, label?: EditionOpenedLabel) {
        track(TrackingEvents.PROFILE_UPDATE_CARD_VIEWED, {
            profile_section: section,
            label,
        });
    }

    function trackProfileSocialShareStarted(socialNetwork: SocialShareEntryPoint, accountId: string, isOwner: boolean = false) {
        track(TrackingEvents.PROFILE_SOCIAL_SHARE_STARTED, {
            entry_point: socialNetwork,
            freelancer_account_id: accountId,
            is_owner: isOwner,
        });
    }

    function trackProfileSocialShareImageDownloaded(accountId: string) {
        track(TrackingEvents.PROFILE_SOCIAL_SHARE_IMAGE_DOWNLOADED, {
            freelancer_account_id: accountId,
        });
    }

    function trackProfileSocialShareOpened(accountId: string, isOwner: boolean = false) {
        track(TrackingEvents.PROFILE_SOCIAL_SHARE_OPENED, {
            freelancer_account_id: accountId,
            is_owner: isOwner,
        });
    }

    function trackProfileUpdated(section: ProfileUpdatedSection, existingElement: boolean) {
        track(TrackingEvents.PROFILE_UPDATED, {
            profile_section: section,
            existing_element: existingElement,
            element_status: existingElement ? ProfileUpdatedElementStatus.UPDATED : ProfileUpdatedElementStatus.CREATED,
        });
    }

    function trackProfileSectionReached(
        section: ProfileSectionReachedSection,
        entryPoint: ProfileSectionReachedEntryPoint,
        profileId: string | undefined,
        freelancerAccountId: string | undefined,
        context: ProfileSectionReachedContext,
    ) {
        track(TrackingEvents.PROFILE_SECTION_REACHED, {
            section: section,
            entry_point: entryPoint,
            profile_id: profileId,
            freelancer_account_id: freelancerAccountId,
            context: context,
        });
    }

    function trackInfluenceLinkClicked(linkType: InfluenceLinkType, profileId: string) {
        track(TrackingEvents.INFLUENCE_LINK_CLICKED, {
            link_type: linkType,
            profile_id: profileId,
        });
    }

    function trackRecommendationRequestStarted(profileId: string, entryPoint: RecommendationRequestStartedEntryPoint) {
        track(TrackingEvents.RECOMMENDATION_REQUEST_STARTED, {
            profile_id: profileId,
            entry_point: entryPoint,
        });
    }

    function trackRecommendationLeft(experienceId: string | undefined, profileId: string) {
        track(TrackingEvents.RECOMMENDATION_LEFT, {
            experience_id: experienceId,
            profile_id: profileId,
        });
    }

    function trackRecommendationDeleted(profileId: string, deletedBy: RecommendationDeletedBy, experienceId: string | undefined) {
        track(TrackingEvents.RECOMMENDATION_DELETED, {
            profile_id: profileId,
            deleted_by: deletedBy,
            experience_id: experienceId,
        });
    }

    function trackRecommendationEmailLinkOpened(profileId: string, recommenderStatus: RecommendationEmailLinkOpenedRecommenderStatus) {
        track(TrackingEvents.RECOMMENDATION_EMAIL_LINK_OPENED, {
            profile_id: profileId,
            recommender_status: recommenderStatus,
        });
    }

    function trackAskProfileRecommendationSent(
        profileId: string,
        entryPoint: RecommendationRequestStartedEntryPoint | undefined,
        numberEmailsAddedToInvitation: number,
        experienceId: string | null,
    ) {
        track(TrackingEvents.RECOMMENDATION_REQUEST_SENT, {
            profile_id: profileId,
            recommendation_entrypoint: entryPoint,
            number_emails_added_to_invitation: numberEmailsAddedToInvitation,
            experience_id: experienceId,
        });
    }

    function trackProfilePortfolioSectionClicked(profileId: string, nbElementsViewed: number) {
        track(TrackingEvents.PROFILE_PORTFOLIO_SECTION_CLICKED, {
            profile_id: profileId,
            nb_elements_viewed: nbElementsViewed,
        });
    }

    function compareCopiedTextWithFreelancerName(freelancerName: string) {
        if (import.meta.client) {
            const getSelection = window.getSelection || document.getSelection;
            const selection = !getSelection ? '' : getSelection()?.toString() || '';
            if (!!selection && freelancerName.includes(selection)) {
                track('freelancer_name_selection_in_profile_page');
            }
        }
    }

    function toEditionOpenedLabel(label: CompletionStatus): EditionOpenedLabel {
        switch (label) {
            case 'INVALID':
                return EditionOpenedLabel.INVALID;
            case 'INCOMPLETE':
                return EditionOpenedLabel.MANDATORY_INFORMATION_MISSING;
            case 'CAN_BE_ENHANCED':
                return EditionOpenedLabel.CAN_BE_ENHANCED;
            case 'ALL_SET':
                return EditionOpenedLabel.WELL_DONE;
        }
    }

    function trackWishListOpened(freelancerAccountId: string | undefined, source: string = 'profile_page') {
        return track('client_favorite_heart_started', {freelancer_account_id: freelancerAccountId, source});
    }

    function trackFreelancerAddedToWishList(freelancerAccountId: string | undefined, source: string = 'profile_page') {
        return track('client_favorite_saved', {freelancer_account_id: freelancerAccountId, source, action: 'added'});
    }

    function trackFreelancerRemovedFromWishList(freelancerAccountId: string | undefined, source: string = 'profile_page') {
        return track('client_favorite_saved', {freelancer_account_id: freelancerAccountId, source, action: 'removed'});
    }

    return {
        trackAISummaryFeedbackClicked,
        trackAISummaryFeedbackSubmitted,
        trackAISummaryStateAction,
        trackAISummaryViewed,
        trackAskProfileRecommendationSent,
        trackFAQOpened,
        trackInfluenceLinkClicked,
        trackProfileEdition,
        trackProfilePortfolioSectionClicked,
        trackProfileSectionReached,
        trackProfileTranslated,
        trackProfileUpdateCardViewed,
        trackProfileSocialShareStarted,
        trackProfileSocialShareOpened,
        trackProfileSocialShareImageDownloaded,
        trackProfileUpdated,
        trackProfileViewed,
        trackRecommendationRequestStarted,
        trackRecommendationLeft,
        trackRecommendationDeleted,
        trackRecommendationEmailLinkOpened,
        compareCopiedTextWithFreelancerName,
        toEditionOpenedLabel,
        trackWishListOpened,
        trackFreelancerAddedToWishList,
        trackFreelancerRemovedFromWishList,
    };
}
